/**
 * Centralized Configuration System for Scholarly Repository
 *
 * This module provides a unified configuration system that can be customized
 * through environment variables. It is designed to make the repository software
 * reusable for any scholarly publication collection.
 *
 * ## Usage
 *
 * Import the CONFIG object in any module:
 * ```js
 * import { CONFIG } from '$lib/config/index.js';
 * console.log(CONFIG.site.name); // "DCPapers"
 * ```
 *
 * ## Customization
 *
 * Set environment variables in `.env` or `.env.local`:
 * ```
 * PUBLIC_SITE_NAME=MyRepository
 * PUBLIC_SITE_URL=https://myrepo.example.org
 * ```
 *
 * @module lib/config
 */

import { env } from '$env/dynamic/public';

/**
 * Get environment variable with fallback
 * @param {`PUBLIC_${string}`} key - Environment variable name (must start with PUBLIC_)
 * @param {string} [fallback=''] - Default value if not set
 * @returns {string} Environment variable value or fallback
 */
const getEnv = (key, fallback = '') => env[key] ?? fallback;

// =============================================================================
// Site Configuration
// =============================================================================

/**
 * Site/application configuration
 * @typedef {Object} SiteConfig
 * @property {string} name - Short site name (e.g., "DCPapers")
 * @property {string} fullName - Full site name with tagline
 * @property {string} url - Base URL without trailing slash
 * @property {string} description - Site description for meta tags
 * @property {string} language - Default content language (ISO 639-1)
 */

/** @type {SiteConfig} */
const site = {
	name: getEnv('PUBLIC_SITE_NAME', 'DCPapers'),
	fullName: getEnv('PUBLIC_SITE_NAME')
		? `${getEnv('PUBLIC_SITE_NAME')} - Conference Proceedings`
		: 'DCPapers - Dublin Core Conference Proceedings',
	url: getEnv('PUBLIC_SITE_URL', 'https://dcpapers.dublincore.org').replace(/\/$/, ''),
	description: getEnv(
		'PUBLIC_SITE_DESCRIPTION',
		'Searchable repository of Dublin Core Metadata Initiative (DCMI) conference proceedings from 2001-2024'
	),
	language: 'en'
};

// =============================================================================
// Publisher Configuration
// =============================================================================

/**
 * Publisher/organization configuration
 * @typedef {Object} PublisherConfig
 * @property {string} name - Organization name
 * @property {string} url - Organization website URL
 * @property {string} email - Admin/contact email
 * @property {string} logoUrl - URL to organization logo (optional)
 */

/** @type {PublisherConfig} */
const publisher = {
	name: getEnv('PUBLIC_PUBLISHER_NAME', 'Dublin Core Metadata Initiative'),
	url: getEnv('PUBLIC_PUBLISHER_URL', 'https://www.dublincore.org/'),
	email: getEnv('PUBLIC_ADMIN_EMAIL', 'dcpapers@dublincore.org'),
	logoUrl: getEnv('PUBLIC_PUBLISHER_URL')
		? `${getEnv('PUBLIC_PUBLISHER_URL')}images/logo.svg`
		: 'https://www.dublincore.org/images/dcmi_logo_v802.svg'
};

// =============================================================================
// Data/API Configuration
// =============================================================================

/**
 * Data source configuration
 * @typedef {Object} DataConfig
 * @property {string} url - Base URL for data API or local path
 * @property {string} filesUrl - Base URL for file storage (PDFs, etc.)
 * @property {string} pdfPrefix - Prefix for PDF filenames (e.g., "dcmi-")
 */

/** @type {DataConfig} */
const data = {
	url: getEnv(
		'PUBLIC_DATA_URL',
		getEnv('PUBLIC_DCPAPERS_DATA_URL', 'https://dcpapers-data.dublincore.org')
	),
	filesUrl: getEnv('PUBLIC_FILES_URL', 'https://dcpapers-data.dublincore.org'),
	pdfPrefix: getEnv('PUBLIC_PDF_PREFIX', 'dcmi-')
};

// =============================================================================
// License Configuration
// =============================================================================

/**
 * License configuration for content
 * @typedef {Object} LicenseConfig
 * @property {string} url - Default license URL
 * @property {string} label - Human-readable license label
 * @property {string} metadataLicense - License for metadata (usually CC0)
 * @property {string} metadataLicenseLabel - Label for metadata license
 * @property {string} openAccessUrl - What the open-access mark links to
 */

/** @type {LicenseConfig} */
const license = {
	url: getEnv('PUBLIC_DEFAULT_LICENSE_URL', 'https://creativecommons.org/licenses/by/4.0/'),
	label: getEnv('PUBLIC_DEFAULT_LICENSE_LABEL', 'CC BY 4.0'),
	metadataLicense: 'https://creativecommons.org/publicdomain/zero/1.0/',
	metadataLicenseLabel: 'CC0 1.0',
	// The open-access mark points at the definition of the term rather than at our
	// own licence page: the padlock asserts a publishing model, the CC button
	// asserts the reuse terms, and they are different claims.
	//
	// UNESCO rather than the Budapest Open Access Initiative, because the label
	// says *Diamond* open access. BOAI (2002) is the founding statement of open
	// access in general and does not define the diamond model; UNESCO's page does,
	// and UNESCO is the body that has carried diamond OA into policy through the
	// 2021 Recommendation on Open Science. Linking a specific claim to a general
	// definition was the looser fit.
	openAccessUrl: getEnv('PUBLIC_OPEN_ACCESS_URL', 'https://www.unesco.org/en/diamond-open-access')
};

// =============================================================================
// Conference/Publication Configuration
// =============================================================================

/**
 * Conference/publication series configuration
 * @typedef {Object} ConferenceConfig
 * @property {string} name - Conference series name
 * @property {string} shortName - Abbreviated conference name
 * @property {string|null} issn - ISSN for the proceedings (if assigned)
 * @property {string} currentIssueId - Issue ID of the current/latest proceedings
 */

/** @type {ConferenceConfig} */
const conference = {
	name: getEnv(
		'PUBLIC_CONFERENCE_NAME',
		'International Conference on Dublin Core and Metadata Applications'
	),
	shortName: 'DC Conference',
	issn: getEnv('PUBLIC_ISSN', '1939-1366'),
	currentIssueId: getEnv('PUBLIC_CURRENT_ISSUE_ID', 'dcmi-2026')
};

// =============================================================================
// OAI-PMH Configuration
// =============================================================================

/**
 * OAI-PMH protocol configuration
 * @typedef {Object} OaiConfig
 * @property {string} repositoryIdentifier - Unique repository identifier
 * @property {string} identifierPrefix - OAI identifier prefix (oai:domain:)
 * @property {string} sampleIdentifier - Example identifier for documentation
 * @property {string} granularity - Date granularity (YYYY-MM-DD or YYYY-MM-DDThh:mm:ssZ)
 * @property {string} deletedRecord - Deleted record policy (no, transient, persistent)
 * @property {string} earliestDatestamp - Earliest record date in repository
 * @property {number} pageSize - Records per page for list operations
 */

/** @type {OaiConfig} */
const oai = {
	repositoryIdentifier: getEnv('PUBLIC_OAI_REPOSITORY_ID', 'dcpapers.dublincore.org'),
	identifierPrefix: getEnv('PUBLIC_OAI_IDENTIFIER_PREFIX', 'oai:dcpapers.dublincore.org:'),
	sampleIdentifier: getEnv('PUBLIC_OAI_IDENTIFIER_PREFIX')
		? `${getEnv('PUBLIC_OAI_IDENTIFIER_PREFIX')}952401345`
		: 'oai:dcpapers.dublincore.org:952401345',
	granularity: 'YYYY-MM-DD',
	deletedRecord: 'no',
	earliestDatestamp: getEnv('PUBLIC_OAI_EARLIEST_DATESTAMP', '2001-10-24'),
	pageSize: parseInt(getEnv('PUBLIC_OAI_PAGE_SIZE', '100'), 10)
};

/**
 * Supported OAI-PMH metadata formats
 * @type {Array<{prefix: string, schema: string, namespace: string}>}
 */
const metadataFormats = [
	{
		prefix: 'oai_dc',
		schema: 'http://www.openarchives.org/OAI/2.0/oai_dc.xsd',
		namespace: 'http://www.openarchives.org/OAI/2.0/oai_dc/'
	}
];

// =============================================================================
// External Services Configuration
// =============================================================================

/**
 * External service URL configuration
 * @typedef {Object} ExternalServicesConfig
 * @property {string} orcid - ORCID URL prefix
 * @property {string} doi - DOI URL prefix
 * @property {string} scopus - Scopus base URL
 * @property {string} scopusPartnerId - Scopus partner ID for links
 * @property {string} wikidata - Wikidata base URL
 * @property {string} semanticScholar - Semantic Scholar base URL
 * @property {string} dblp - DBLP base URL
 * @property {string} googleScholar - Google Scholar base URL
 */

/** @type {ExternalServicesConfig} */
const externalServices = {
	orcid: 'https://orcid.org/',
	doi: 'https://doi.org/',
	scopus: 'https://www.scopus.com/',
	scopusPartnerId: 'HzOxMe3b',
	wikidata: 'https://www.wikidata.org/wiki/',
	semanticScholar: 'https://www.semanticscholar.org/',
	dblp: 'https://dblp.org/',
	googleScholar: 'https://scholar.google.com/'
};

// =============================================================================
// Schema.org Types
// =============================================================================

/**
 * Schema.org type URLs for structured data
 * @type {Object<string, string>}
 */
const schemaTypes = {
	scholarlyArticle: 'https://schema.org/ScholarlyArticle',
	organization: 'https://schema.org/Organization',
	person: 'https://schema.org/Person',
	periodical: 'https://schema.org/Periodical',
	publicationIssue: 'https://schema.org/PublicationIssue'
};

// =============================================================================
// MIME Types
// =============================================================================

/**
 * MIME types for common content formats
 * @type {Object<string, string>}
 */
const mimeTypes = {
	pdf: 'application/pdf',
	html: 'text/html',
	xml: 'application/xml',
	json: 'application/json',
	linksetJson: 'application/linkset+json'
};

// =============================================================================
// Cache Configuration
// =============================================================================

/**
 * Cache control durations (in seconds)
 * @type {Object<string, number>}
 */
const cacheDurations = {
	/** Static content like sitemaps, resource lists (1 day) */
	static: 86400,
	/** Dynamic content like OAI-PMH responses (1 hour) */
	dynamic: 3600,
	/** Rarely changing content (1 week) */
	long: 604800
};

// =============================================================================
// Main Configuration Export
// =============================================================================

/**
 * Complete application configuration
 *
 * @type {{
 *   site: SiteConfig,
 *   publisher: PublisherConfig,
 *   data: DataConfig,
 *   license: LicenseConfig,
 *   conference: ConferenceConfig,
 *   oai: OaiConfig,
 *   metadataFormats: Array<{prefix: string, schema: string, namespace: string}>,
 *   externalServices: ExternalServicesConfig,
 *   schemaTypes: Object<string, string>,
 *   mimeTypes: Object<string, string>,
 *   cacheDurations: Object<string, number>
 * }}
 */
export const CONFIG = {
	site,
	publisher,
	data,
	license,
	conference,
	oai,
	metadataFormats,
	externalServices,
	schemaTypes,
	mimeTypes,
	cacheDurations
};

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get full URL for an article page
 * @param {string} articleId - Article identifier
 * @returns {string} Full article URL
 */
export function getArticleUrl(articleId) {
	return `${CONFIG.site.url}/article/${articleId}`;
}

/**
 * Get full URL for an issue page
 * @param {string} issueId - Issue identifier
 * @returns {string} Full issue URL
 */
export function getIssueUrl(issueId) {
	return `${CONFIG.site.url}/issue/${issueId}`;
}

/**
 * Get PDF URL for an article
 * @param {string} articleId - Article identifier
 * @param {string} articlePath - Path to article in data structure
 * @returns {string} Full PDF URL
 */
export function getPdfUrl(articleId, articlePath) {
	return `${CONFIG.data.filesUrl}/${articlePath}/files/${CONFIG.data.pdfPrefix}${articleId}.pdf`;
}

/**
 * Get download filename for a PDF
 * @param {string} articleId - Article identifier
 * @returns {string} PDF filename for download
 */
export function getPdfFilename(articleId) {
	return `${CONFIG.data.pdfPrefix}${articleId}.pdf`;
}

/**
 * Get OAI identifier for an article
 * @param {string} articleId - Article identifier
 * @returns {string} Full OAI identifier
 */
export function getOaiIdentifier(articleId) {
	return `${CONFIG.oai.identifierPrefix}${articleId}`;
}

/**
 * Extract article ID from OAI identifier
 * @param {string} oaiIdentifier - Full OAI identifier
 * @returns {string|null} Article ID or null if invalid format
 */
export function extractArticleIdFromOai(oaiIdentifier) {
	const prefix = CONFIG.oai.identifierPrefix;
	if (oaiIdentifier.startsWith(prefix)) {
		return oaiIdentifier.slice(prefix.length);
	}
	return null;
}

/**
 * Get Scopus record URL
 * @param {string} scopusId - Scopus EID
 * @returns {string} Full Scopus URL
 */
export function getScopusUrl(scopusId) {
	const { scopus, scopusPartnerId } = CONFIG.externalServices;
	return `${scopus}inward/record.uri?partnerID=${scopusPartnerId}&scp=${scopusId}&origin=inward`;
}

/**
 * Get Wikidata entity URL
 * @param {string} wikidataId - Wikidata Q-number
 * @returns {string} Full Wikidata URL
 */
export function getWikidataUrl(wikidataId) {
	return `${CONFIG.externalServices.wikidata}${wikidataId}`;
}

/**
 * Get Semantic Scholar paper URL
 * @param {string} semanticScholarId - Semantic Scholar ID
 * @returns {string} Full Semantic Scholar URL
 */
export function getSemanticScholarUrl(semanticScholarId) {
	return `${CONFIG.externalServices.semanticScholar}paper/${semanticScholarId}`;
}

/**
 * Get DBLP record URL
 * @param {string} dblpId - DBLP key
 * @returns {string} Full DBLP URL
 */
export function getDblpUrl(dblpId) {
	return `${CONFIG.externalServices.dblp}rec/${dblpId}`;
}

/**
 * Get Google Scholar author search URL
 * @param {string} name - Author name to search
 * @returns {string} Google Scholar search URL
 */
export function getGoogleScholarAuthorUrl(name) {
	return `${CONFIG.externalServices.googleScholar}scholar?q=author:"${encodeURIComponent(name)}"`;
}

/**
 * Get Semantic Scholar author search URL
 * @param {string} name - Author name to search
 * @returns {string} Semantic Scholar search URL
 */
export function getSemanticScholarAuthorUrl(name) {
	return `${CONFIG.externalServices.semanticScholar}search?q=${encodeURIComponent(name)}&sort=relevance`;
}

/**
 * Get ORCID URL from identifier
 * @param {string} orcidId - ORCID identifier (with or without URL prefix)
 * @returns {string} Full ORCID URL
 */
export function getOrcidUrl(orcidId) {
	if (!orcidId) return '';
	if (orcidId.startsWith('https://orcid.org/')) return orcidId;
	if (orcidId.startsWith('http://orcid.org/')) return orcidId.replace('http://', 'https://');
	return `${CONFIG.externalServices.orcid}${orcidId}`;
}

/**
 * Get DOI URL from identifier
 * @param {string} doi - DOI (with or without URL prefix)
 * @returns {string} Full DOI URL
 */
export function getDoiUrl(doi) {
	if (!doi) return '';
	if (doi.startsWith('https://doi.org/')) return doi;
	if (doi.startsWith('http://doi.org/')) return doi.replace('http://', 'https://');
	return `${CONFIG.externalServices.doi}${doi}`;
}

/**
 * Get the ISSN Portal record URL for an ISSN.
 *
 * Derived from the identifier rather than stored as a link, so it cannot drift
 * from `CONFIG.conference.issn`. Hyphens are preserved: the portal expects the
 * canonical NNNN-NNNC form.
 *
 * @param {string|null|undefined} issn - ISSN, e.g. "1939-1366"
 * @returns {string} Full ISSN Portal URL, or '' when no ISSN is set
 */
export function getIssnUrl(issn) {
	if (!issn) return '';
	if (/^https?:\/\//.test(issn)) return issn;
	return `https://portal.issn.org/resource/ISSN/${encodeURIComponent(issn.trim())}`;
}

export default CONFIG;
